import React, { useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import Modal from '../components/Modal';
import EventDetail from '../components/EventDetail';
import {
  getUpcomingNights,
  getNextNight,
  formatLongDate,
  hasTheme,
  countdownLabel,
  listingDateLabel,
} from '../data/events';
import { downloadIcs } from '../lib/calendar';
import useInView from '../hooks/useInView';
import useEvents from '../hooks/useEvents';
import usePageMeta from '../hooks/usePageMeta';
import { INSTAGRAM_URL } from '../config';
import styles from './ReviveNights.module.css';

export default function ReviveNights() {
  usePageMeta(
    'Revive Nights — Revive Worship',
    'Once a month we worship in public, anywhere in Pittsburgh. See the upcoming Revive Nights schedule and join us.'
  );

  const events = useEvents();
  const upcoming = useMemo(() => getUpcomingNights(new Date(), events), [events]);
  const next = useMemo(() => getNextNight(new Date(), events), [events]);
  const [openId, setOpenId] = useState(null);
  const [headRef, headIn] = useInView({ threshold: 0.2 });

  const openEvent = events.find((e) => e.id === openId);

  return (
    <>
      {/* Single combined hero: page intro + Up Next, one full-bleed dark
          section over the next-event photo so the page reads as one piece. */}
      <section className={styles.hero}>
        <div className={styles.heroBg}>
          <img src={next?.image || '/images/nights-hero.jpg'} alt="" />
        </div>
        <div className={styles.shell}>
          <div className={styles.heroGrid}>
            <div className={styles.heroIntro}>
              <span className={styles.heroEyebrow}>Revive Nights</span>
              <h1 className={styles.heroTitle}>
                Once a month, we worship in public.
              </h1>
              <p className={styles.heroLede}>
                Once every month, we gather in a public space that is accessible
                to all, to worship Jesus unashamed, with undivided hearts, in
                spirit and in truth. We'd love to worship with you sometime.
              </p>
            </div>

            {next && (
              <div className={styles.upNext}>
                <span className={styles.upNextEyebrow}>
                  {countdownLabel(next)
                    ? `Up Next · ${countdownLabel(next)}`
                    : 'Up Next'}
                </span>
                <h2 className={styles.upNextTitle}>
                  {next.isSabbath
                    ? `${next.edition}.`
                    : `Revive Night: ${next.edition}.`}
                </h2>

                <dl className={styles.nextMeta}>
                  {next.theme && next.theme !== 'TBD' && (
                    <div className={styles.nextMetaRow}>
                      <dt>Theme</dt>
                      <dd>{next.theme}</dd>
                    </div>
                  )}
                  <div className={styles.nextMetaRow}>
                    <dt>Date</dt>
                    <dd>{next.tbd ? 'To be announced' : formatLongDate(next.date)}</dd>
                  </div>
                  <div className={styles.nextMetaRow}>
                    <dt>Time</dt>
                    <dd>{next.timeLabel}</dd>
                  </div>
                  {next.locationName && (
                    <div className={styles.nextMetaRow}>
                      <dt>Location</dt>
                      <dd>
                        {next.locationUrl ? (
                          <a className={styles.nextMetaLink} href={next.locationUrl} target="_blank" rel="noreferrer">
                            {next.locationName} <span aria-hidden="true">↗</span>
                          </a>
                        ) : (
                          next.locationName
                        )}
                      </dd>
                    </div>
                  )}
                </dl>

                <div className={styles.nextActions}>
                  <button
                    className={styles.nextCta}
                    onClick={() => setOpenId(next.id)}
                  >
                    View full details
                    <span aria-hidden="true">→</span>
                  </button>
                  {!next.tbd && !next.isSabbath && (
                    <button
                      className={`${styles.nextCta} ${styles.nextCtaGhost}`}
                      onClick={() => downloadIcs(next)}
                    >
                      Add to calendar
                      <span aria-hidden="true">⤓</span>
                    </button>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Full month listing */}
      <section className={styles.allBlock}>
        <div className={styles.shell}>
          <header className={styles.allHead} ref={headRef}>
            <h2 className={`${styles.allTitle} ${headIn ? styles.allTitleIn : ''}`}>
              <span className={styles.allTitleLine}>
                <span className={styles.allTitleInner}>The 2026 season.</span>
              </span>
            </h2>
            <p className={styles.allLede}>
              One night a month, somewhere in Pittsburgh. Tap any month for
              details, directions, and a calendar invite.
            </p>
          </header>

          <ol className={styles.list}>
            {[...events].sort((a, b) => new Date(a.date) - new Date(b.date)).map((ev, i) => {
              const isUpcoming = upcoming.some((u) => u.id === ev.id);
              const isNext = ev.id === next?.id;
              return (
                <motion.li
                  key={ev.id}
                  className={`${styles.row} ${isUpcoming ? '' : styles.rowPast} ${isNext ? styles.rowNext : ''}`}
                  initial={{ opacity: 0, y: 18 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, amount: 0.2 }}
                  transition={{ duration: 0.6, delay: i * 0.04, ease: [0.22, 1, 0.36, 1] }}
                >
                  <button
                    className={styles.rowBtn}
                    onClick={() => setOpenId(ev.id)}
                  >
                    <span className={styles.rowMonth}>{ev.monthLabel}</span>
                    <span className={styles.rowDate}>{listingDateLabel(ev)}</span>
                    <span className={styles.rowLocation}>
                      {ev.isSabbath
                        ? 'No public gathering'
                        : ev.locationName && ev.locationName !== 'TBD'
                          ? ev.locationName
                          : 'Venue TBD'}
                    </span>
                    <span className={styles.rowTheme}>
                      {ev.isSabbath ? ev.theme : hasTheme(ev) ? ev.theme : ''}
                    </span>
                    <span className={styles.rowArrow} aria-hidden="true">↗</span>
                  </button>
                </motion.li>
              );
            })}
          </ol>

          <div className={styles.followStrip}>
            <p className={styles.followCopy}>
              Connect with us on Instagram.
            </p>
            <a className={styles.followBtn} href={INSTAGRAM_URL} target="_blank" rel="noreferrer">
              @revive.worship_
              <span aria-hidden="true">↗</span>
            </a>
          </div>
        </div>
      </section>

      <AnimatePresence>
        {openEvent && (
          <Modal open onClose={() => setOpenId(null)} label={openEvent.edition} size="md">
            <EventDetail event={openEvent} />
          </Modal>
        )}
      </AnimatePresence>
    </>
  );
}
